import React, { Component, type ComponentType } from 'react';
import Button, { type Appearance } from '@atlaskit/button';
import { ModalBody, ModalHeader, ModalTitle, ModalFooter } from '@atlaskit/modal-dialog';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { FlagService } from '@atlassian/jira-flags';
import type { IntlShape } from '@atlassian/jira-intl';
import { JiraModal as ModalDialog } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import type { NavigationItem } from '@atlassian/jira-navigation-types/src/types.tsx';
import { REPO_TYPE } from '../../common/const.tsx';
import { successFlag, errorFlag } from '../flags/index.tsx';
import addRepository from '../services/add-repository/index.tsx';
import messages from './messages.tsx';

type Props = {
	projectId: string;
	baseUrl: string;
	flagService: FlagService;
	Content: ComponentType<{
		onDataChange: (arg1: { url: string; name: string; hasError?: boolean }) => void;
	}>;
	onCompleted: (arg1: { item: NavigationItem | undefined }) => void;
	intl: IntlShape;
};

type State = {
	url: string;
	name: string;
	isInProgress: boolean;
};

// eslint-disable-next-line jira/react/no-class-components
class AddRepositoryModal extends Component<Props, State> {
	state = {
		url: '',
		name: '',
		isInProgress: false,
	};

	onClose = () => {
		const { onCompleted } = this.props;
		onCompleted({ item: undefined });
	};

	onAdd = () => {
		this.performAdd();
	};

	onDataChange = ({ url, name }: { url: string; name: string; hasError?: boolean }) => {
		this.setState({ url, name });
	};

	async performAdd() {
		const { projectId, baseUrl, onCompleted, flagService } = this.props;
		const { name, url } = this.state;

		try {
			this.setState({ isInProgress: true });
			const { id } = await addRepository(baseUrl, {
				projectId,
				name,
				url,
				icon: '',
				type: 'source.code.link',
			});

			this.setState({ isInProgress: false });
			onCompleted({
				item: {
					type: 'Item',
					id,
					text: name,
					href: url,
					shortcutType: REPO_TYPE,
				},
			});
			// @ts-expect-error - TS2345 - Argument of type '{ title: { id: string; defaultMessage: string; description: string; }; description: { id: string; defaultMessage: string; description: string; }; type: string; }' is not assignable to parameter of type 'Flag'.
			flagService.showFlag(successFlag());
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (e: any) {
			// @ts-expect-error - TS2345 - Argument of type '{ title: { id: string; defaultMessage: string; description: string; }; description: { id: string; defaultMessage: string; description: string; }; type: string; }' is not assignable to parameter of type 'Flag'.
			flagService.showFlag(errorFlag());
			onCompleted({ item: undefined });
		}
	}

	render() {
		const { Content } = this.props;
		const { isInProgress } = this.state;
		const {
			intl: { formatMessage },
		} = this.props;
		const isSubmitEnabled = !!this.state.url.trim() && !!this.state.name.trim();
		const actions = [
			{
				appearance: 'link',
				onClick: this.onClose,
				text: formatMessage(messages.cancelCaption),
				isDisabled: isInProgress,
			},
			{
				appearance: 'primary',
				onClick: this.onAdd,
				text: formatMessage(messages.submitCaption),
				isDisabled: !isSubmitEnabled || isInProgress,
			},
		];

		return (
			<ShortcutScope>
				<ModalDialog
					onClose={this.onClose}
					autoFocus
					shouldCloseOnOverlayClick={false}
					messageId="navigation-shortcuts.add-repository-modal.modal-dialog"
					messageType="transactional"
				>
					<ModalHeader>
						<ModalTitle>{formatMessage(messages.heading)}</ModalTitle>
					</ModalHeader>
					<ModalBody>
						{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
						<p>
							{formatMessage(
								fg('jira-issue-terminology-refresh-m3')
									? messages.descriptionIssueTermRefresh
									: messages.description,
							)}
						</p>
						<Content onDataChange={this.onDataChange} />
					</ModalBody>
					<ModalFooter>
						{actions.map((action) => (
							<Button
								key={`btn-${action.appearance}`}
								// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
								appearance={action.appearance as Appearance}
								onClick={action.onClick}
								isDisabled={action.isDisabled}
							>
								{action.text}
							</Button>
						))}
					</ModalFooter>
				</ModalDialog>
			</ShortcutScope>
		);
	}
}

export default AddRepositoryModal;
